import { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WidgetGalleryDialog from '../../dummies/WidgetGallery/WidgetGalleryDialog';
import { CenterDialog } from '../../dummies/CenterDialog/CenterDialog';

/**
 * AddWidgetButton - כפתור הוספת ווידג'טים
 * פותח גלריה מטורפת עם כל הווידג'טים הזמינים
 * 
 * Business Component - מקבל callback דרך props
 * 
 * @param {function} onSelectWidget - callback שנקרא כשבוחרים ווידג'ט מקטלוג (הוספה מהירה)
 * @param {function} onCopyWidget - callback שנקרא כשמעתיקים ווידג'ט מדשבורד אחר
 * @param {function} onMoveWidget - callback שנקרא כשגוזרים ווידג'ט מרכיבים שמורים
 * @param {function} onOpenWidgetConfig - callback שנקרא כשרוצים לפתוח קונפיג מלא
 * @param {array} catalog - קטלוג הווידג'טים
 * @param {array} myWidgets - כל הווידג'טים מכל הדשבורדים
 * @param {array} savedWidgets - רכיבים שמורים ברמת ארגון
 * @param {string} currentDashboardId - ID של הדשבורד הנוכחי
 * @param {boolean} fullWidth - האם הכפתור ברוחב מלא
 */
const AddWidgetButton = ({
    onSelectWidget,
    onCopyWidget,
    onMoveWidget,
    onOpenWidgetConfig,
    catalog = [],
    myWidgets = [],
    savedWidgets = [],
    currentDashboardId,
    fullWidth = false
}) => {
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [noSpaceDialogOpen, setNoSpaceDialogOpen] = useState(false);

    const handleOpenGallery = () => {
        setGalleryOpen(true);
    };

    const handleCloseGallery = () => {
        setGalleryOpen(false);
    };

    const handleSelectWidget = async (componentKey, templateId, dashboardLayout, needsConfig) => {
        // אם הווידג'ט צריך קונפיג (כמו TextWidget, NoticeBoardWidget וכו')
        // נקרא ל-onOpenWidgetConfig שיפתח את הדיאלוג המתאים
        if (needsConfig && onOpenWidgetConfig) {
            onOpenWidgetConfig(componentKey, templateId);
            setGalleryOpen(false);
            return;
        }

        // אחרת, הוספה מהירה ישירות לדשבורד
        if (onSelectWidget) {
            const result = await onSelectWidget(componentKey, templateId, dashboardLayout);
            if (result && result.success === false) {
                setNoSpaceDialogOpen(true);
                return;
            }
        }
        setGalleryOpen(false);
    };

    const handleCopyWidget = async (widget) => {
        if (onCopyWidget) {
            const result = await onCopyWidget(widget);
            if (result && result.success === false) {
                return;
            }
        }
        setGalleryOpen(false);
    };

    const handleMoveWidget = async (widget) => {
        if (onMoveWidget) {
            const result = await onMoveWidget(widget);
            if (result && result.success === false) {
                return;
            }
        }
        setGalleryOpen(false);
    };

    return (
        <Box>
            <Button
                fullWidth={fullWidth}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenGallery}
                sx={{
                    borderColor: 'rgba(30,58,95,0.45)',
                    color: '#1e3a5f',
                    fontWeight: 500,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.875rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    minHeight: '36px',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: '#1e3a5f',
                        bgcolor: 'rgba(30,58,95,0.05)',
                        boxShadow: '0 2px 10px rgba(30,58,95,0.15)',
                        transform: 'translateY(-1px)'
                    },
                    gap: 1
                }}
            >
                הוסף רכיב
            </Button>

            <WidgetGalleryDialog
                open={galleryOpen}
                onClose={handleCloseGallery}
                onSelectWidget={handleSelectWidget}
                onCopyWidget={handleCopyWidget}
                onMoveWidget={handleMoveWidget}
                onOpenWidgetConfig={onOpenWidgetConfig}
                catalog={catalog}
                myWidgets={myWidgets}
                savedWidgets={savedWidgets}
                currentDashboardId={currentDashboardId}
            />

            {/* No Space Warning Dialog */}
            <CenterDialog
                open={noSpaceDialogOpen}
                onClose={() => setNoSpaceDialogOpen(false)}
                title="אין מספיק מקום"
                hideActions={true}
                minWidth="320px"
                maxWidth="450px"
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{
                        color: '#475569',
                        fontSize: '0.95rem',
                        mb: 3
                    }}>
                        אין מספיק מקום בדשבורד להוספת הרכיב.<br />
                        נסה למחוק או להקטין רכיבים קיימים.
                    </Typography>
                    <Button
                        onClick={() => setNoSpaceDialogOpen(false)}
                        variant="contained"
                        sx={{
                            bgcolor: '#1e3a5f',
                            color: '#ffffff',
                            boxShadow: '0 2px 8px rgba(30,58,95,0.2)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: '#152844',
                                boxShadow: '0 4px 16px rgba(30,58,95,0.35)',
                                transform: 'translateY(-1px)'
                            },
                            borderRadius: '8px',
                            px: 4,
                            py: 1,
                            fontWeight: 600,
                            minWidth: '120px'
                        }}
                    >
                        הבנתי
                    </Button>
                </Box>
            </CenterDialog>
        </Box>
    );
};

export default AddWidgetButton;
